/**
 * Review Email dialog for the SOP `email.send` action.
 *
 * Opens when the `email.send` handler emits `email_requested`. The rep reviews
 * and edits the LLM-drafted subject + body, then "Send Now" sends it from their
 * connected Gmail via `POST /api/email/send`. If their account isn't connected
 * / Gmail can't send, the dialog surfaces a clear reason + how to connect and
 * a one-click "Copy email" so they can paste it into their own inbox for now.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { Loader2Icon, CopyIcon, CheckIcon } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { fetchApi } from "@/lib/api";

export interface SendEmailDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  to: string;
  subject: string;
  draftedBody: string;
  dealId?: number | null;
  todoKey?: string | null;
  leadFirstName?: string | null;
  onSendComplete?: () => void;
}

interface SendError {
  errorCode?: string;
  message?: string;
}

/** Map a send error to rep-facing guidance. Connection failures get the
 *  "connect / copy-paste" steer the ticket calls for. */
function errorGuidance(err: SendError): string {
  switch (err.errorCode) {
    case "gmail.not_authorized":
    case "gmail.send_denied":
      return (
        "We couldn't send from your Gmail account. Your Workspace admin may need to authorize Hickory to send " +
        "mail on your behalf. For now, copy the email below and send it from your own inbox."
      );
    case "gmail.invalid_recipient":
      return "Gmail rejected the recipient — double-check the address above.";
    case "gmail.api_failed":
      return "Gmail didn't respond. Try again in a moment, or copy the email and send it from your inbox.";
    default:
      return err.message || "Couldn't send the email. You can copy it below and send it from your own inbox.";
  }
}

export function SendEmailDialog({
  open,
  onOpenChange,
  to,
  subject: initialSubject,
  draftedBody,
  dealId,
  todoKey,
  leadFirstName,
  onSendComplete,
}: SendEmailDialogProps) {
  const [subject, setSubject] = useState(initialSubject);
  const [body, setBody] = useState(draftedBody);
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState<SendError | null>(null);
  const [copied, setCopied] = useState(false);
  const wasOpenRef = useRef(false);

  // Re-seed the editable fields each time the dialog opens for a new request.
  useEffect(() => {
    if (open && !wasOpenRef.current) {
      setSubject(initialSubject);
      setBody(draftedBody);
      setError(null);
      setIsSending(false);
      setCopied(false);
    }
    wasOpenRef.current = open;
  }, [open, initialSubject, draftedBody]);

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(`To: ${to}\nSubject: ${subject}\n\n${body}`);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Couldn't copy to clipboard");
    }
  }, [to, subject, body]);

  const handleSend = useCallback(async () => {
    if (!body.trim()) return;
    setIsSending(true);
    setError(null);
    try {
      const result: { is_test?: boolean } = await fetchApi("/email/send", {
        method: "POST",
        body: JSON.stringify({
          to,
          subject,
          body,
          deal_id: dealId ?? null,
          todo_key: todoKey ?? null,
        }),
      });
      // Personal test mode sent it to the sender: the customer never got it, so their to-do stays open.
      if (result?.is_test) {
        toast.success("Test email sent to you.");
      } else {
        toast.success(`Email queued for ${leadFirstName?.trim() || "the customer"} — it'll send shortly.`);
        onSendComplete?.();
      }
    } catch (e) {
      const detail = ((e as { body?: { detail?: { error_code?: string; message?: string } } })?.body?.detail) ?? {};
      setError({ errorCode: detail.error_code, message: detail.message });
    } finally {
      setIsSending(false);
    }
  }, [to, subject, body, dealId, todoKey, leadFirstName, onSendComplete]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>Review email</DialogTitle>
          <DialogDescription>Edit the draft, then send it from your Gmail.</DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-1">
          <div className="space-y-1.5">
            <Label>To</Label>
            <div className="text-sm text-muted-foreground">{to}</div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="email-subject">Subject</Label>
            <Input id="email-subject" value={subject} onChange={(e) => setSubject(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="email-body">Body</Label>
            <Textarea
              id="email-body"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={10}
              className="resize-y"
            />
          </div>

          {error && (
            <div className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2.5 text-sm">
              <p className="font-medium text-destructive">Couldn't send</p>
              <p className="mt-1 text-muted-foreground">{errorGuidance(error)}</p>
            </div>
          )}
        </div>

        <DialogFooter className="gap-2 sm:justify-between">
          <Button type="button" variant="outline" onClick={handleCopy} className="gap-1.5">
            {copied ? <CheckIcon className="h-4 w-4" /> : <CopyIcon className="h-4 w-4" />}
            {copied ? "Copied" : "Copy email"}
          </Button>
          <Button onClick={handleSend} disabled={!body.trim() || isSending} aria-busy={isSending} className="gap-1.5">
            {isSending && <Loader2Icon className="h-4 w-4 animate-spin" />}
            {isSending ? "Sending…" : "Send now"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
